import { useFetchImageList, usePutImageUse } from '@api';
import { optionsConstant, PathnameEnum, ToastMessageEnum } from '@constant';
import {
  useActionGenreActions,
  useAdultsOnlyActions,
  useBodyPartActions,
  useEtcActions,
  useFacialExpressionActions,
  useGeneralActions,
  useStandardActions
} from '@remote-hooks';
import { useSelector } from '@stores';
import { useQueryClient } from '@tanstack/react-query';
import { IImageListItem, IImageModalType, ISelectedImageInfo, ISequenceInfo } from '@types';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useToast } from './useToast';

//Output페이지 및 GET /images 로직에 관여
export const useOutput = () => {
  const [isImageModalVisible, setIsImageModalVisible] = useState<boolean>(false);
  const [imageModalType, setImageModalType] = useState<IImageModalType>({ needSequence: false, needSimilar: false });
  const [selectedImageInfo, setSelectedImageInfo] = useState<ISelectedImageInfo | null>(null);
  const [sequenceInfo, setSequenceInfo] = useState<ISequenceInfo | null>(null);

  const storedComic = useSelector((state) => state.comic);
  const storedGeneralChildOptionValue = useSelector((state) => state.general);
  const storedStandardChildOptionValue = useSelector((state) => state.standard);
  const storedAdultsOnlyChildOptionValue = useSelector((state) => state.adultsOnly);
  const storedActionGenre = useSelector((state) => state.actionGenre);
  const storedFacialExpressionChildOptionValue = useSelector((state) => state.facialExpression);
  const storedBodyPartChildOptionValue = useSelector((state) => state.bodyPart);
  const storedEtcChildOptionValue = useSelector((state) => state.etc);

  //comic: 장르 및 작품명
  const storedGenreList = storedComic.genreList;
  const storedComicNameList = storedComic.comicNameList;

  //general
  const maleNumber = storedGeneralChildOptionValue.maleNumber;
  const femaleNumber = storedGeneralChildOptionValue.femaleNumber;
  const wholeGenderNumber = storedGeneralChildOptionValue.wholeGenderNumber;
  const isWholeGenderNumberChecked = storedGeneralChildOptionValue.isWholeGenderNumberChecked;

  const [characterNumber, setCharacterNumber] = useState<{
    maleNumber: number | string;
    femaleNumber: number | string;
    wholeGenderNumber: number | string;
    isWholeGenderNumberChecked: boolean;
  }>({
    maleNumber: maleNumber,
    femaleNumber: femaleNumber,
    wholeGenderNumber: wholeGenderNumber,
    isWholeGenderNumberChecked: isWholeGenderNumberChecked
  });

  //special - action genre
  const actionGenreChildOptionValue = storedActionGenre.childOptionValue;

  const { onSetGeneral } = useGeneralActions();
  const { onSetStandard } = useStandardActions();
  const { onSetAdultsOnly } = useAdultsOnlyActions();
  const { onSetActionGenre } = useActionGenreActions();
  const { onSetBodyPart } = useBodyPartActions();
  const { onSetFacialExpression } = useFacialExpressionActions();
  const { onSetEtc } = useEtcActions();

  const { showToast } = useToast();

  const outputBottomRef = useRef<HTMLDivElement | null>(null);
  const outputBottomRefCurrent = outputBottomRef.current;
  const location = useLocation();
  const locationPathname = location.pathname;
  const queryClient = useQueryClient();

  const createQueryStringForImageList = () => {
    const temp = [];
    const standardSearchOutputTake = 21;
    const otherOutputTake = 18;

    //스탠다드 검색
    if (locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE) {
      temp.push(`&take=${standardSearchOutputTake}`);

      //장르 및 작품명
      if (storedGenreList.length === 0 && storedComicNameList.length === 0) {
        return '';
      } else {
        //장르
        if (storedGenreList.length > 0) {
          const selectedGenreStr = storedGenreList.join('^');
          temp.push(`&genre=${selectedGenreStr}`);
        }
        //작품명
        if (storedComicNameList.length > 0) {
          const selectedComicStr = storedComicNameList.join('^');
          temp.push(`&comic=${selectedComicStr}`);
        }
      }

      // 내가 사용한 이미지 포함 여부
      temp.push(`&isIncludeUsed=${storedStandardChildOptionValue.isIncludeUsed}`);

      // 남이 사용한 이미지 포함 여부
      temp.push(`&isIncludeOtherUsersUsed=${storedStandardChildOptionValue.isIncludeOtherUsersUsed}`);

      // 정렬 기준
      temp.push(`&sortType=${storedStandardChildOptionValue.sortType}`);

      //parentOption이 GENERAL인 옵션에 대한 query param 생성
      //parentOption이 GENERAL이 아닌 옵션에 대한 query param보다 앞 쪽에 위치하여야 한다 (14차 스프린트 사항)
      //인원수, 샷 구분, 가로앵글, 세로앵글
      //general이 아닌 standard store에서 가져온 값
      if (
        storedStandardChildOptionValue.characterNumber ||
        storedStandardChildOptionValue.frame ||
        storedStandardChildOptionValue.horizontalAngle ||
        storedStandardChildOptionValue.verticalAngle
      ) {
        temp.push(`&parentOptions=${optionsConstant.parentOptions['GENERAL'].id}`);

        //인원수
        if (storedStandardChildOptionValue.characterNumber) {
          if (storedStandardChildOptionValue.characterNumber.isWholeGenderNumberChecked) {
            temp.push(
              `&prop.${optionsConstant.childOptions['wholeGenderNumber'].id}=${storedStandardChildOptionValue.characterNumber.wholeGenderNumber === '4+' ? '다수' : storedStandardChildOptionValue.characterNumber.wholeGenderNumber}`
            );
          } else {
            temp.push(
              `&prop.${
                optionsConstant.childOptions['maleNumber'].id
              }=${storedStandardChildOptionValue.characterNumber.maleNumber === '4+' ? '다수' : storedStandardChildOptionValue.characterNumber.maleNumber}&prop.${optionsConstant.childOptions['femaleNumber'].id}=${storedStandardChildOptionValue.characterNumber.femaleNumber === '4+' ? '다수' : storedStandardChildOptionValue.characterNumber.femaleNumber}`
            );
          }
        }

        //프레임
        if (storedStandardChildOptionValue.frame) {
          const selectedFrameChildOptionValuesArr = Object.values(storedStandardChildOptionValue.frame)
            .filter((item) => item.isSelected)
            .map((item) => item.value);

          //선택한 프레임 값이 1개 이상일 경우에만 진행
          if (selectedFrameChildOptionValuesArr.length > 0) {
            //프레임 값
            const selectedFrameChildOptionValuesStr = selectedFrameChildOptionValuesArr.join('^');
            temp.push(`&prop.${optionsConstant.childOptions['shot'].id}=${selectedFrameChildOptionValuesStr}`);
          }
        }

        //가로앵글
        if (storedStandardChildOptionValue.horizontalAngle) {
          const selectedHorizontalAngleChildOptionValuesArr = Object.values(
            storedStandardChildOptionValue.horizontalAngle
          )
            .filter((item) => item.isSelected)
            .map((item) => item.value);

          //선택한 가로앵글 값이 1개 이상일 경우에만 진행
          if (selectedHorizontalAngleChildOptionValuesArr.length > 0) {
            //가로앵글 값
            const selectedHorizontalAngleChildOptionValuesStr = selectedHorizontalAngleChildOptionValuesArr.join('^');
            temp.push(
              `&prop.${optionsConstant.childOptions['angleFirst'].id}=${selectedHorizontalAngleChildOptionValuesStr}`
            );
          }
        }

        //세로앵글
        if (storedStandardChildOptionValue.verticalAngle) {
          const selectedVerticalAngleChildOptionValuesArr = Object.values(storedStandardChildOptionValue.verticalAngle)
            .filter((item) => item.isSelected)
            .map((item) => item.value);

          //선택한 세로앵글 값이 1개 이상일 경우에만 진행
          if (selectedVerticalAngleChildOptionValuesArr.length > 0) {
            //세로앵글 값
            const selectedVerticalAngleChildOptionValuesStr = selectedVerticalAngleChildOptionValuesArr.join('^');
            temp.push(
              `&prop.${optionsConstant.childOptions['angleSecond'].id}=${selectedVerticalAngleChildOptionValuesStr}`
            );
          }
        }
      }

      //parentOption={스탠다드}
      temp.push(`&parentOptions=${optionsConstant.parentOptions['STANDARD'].id}`);

      // 배경컷
      if (storedStandardChildOptionValue.backgroundCut) {
        const selectedOption = Object.values(storedStandardChildOptionValue.backgroundCut).find(
          (option) => option.isSelected
        );

        if (selectedOption?.value) {
          temp.push(`&prop.${optionsConstant.childOptions['backgroundCut'].id}=${selectedOption.value}`);
        }
      }

      // 연출
      if (storedStandardChildOptionValue.directed) {
        const selectedOption = Object.values(storedStandardChildOptionValue.directed).find(
          (option) => option.isSelected
        );

        if (selectedOption?.value) {
          temp.push(`&prop.${optionsConstant.childOptions['directed'].id}=${selectedOption.value}`);
        }
      }

      //데포르메
      if (storedStandardChildOptionValue.deforme) {
        const selectedOption = Object.values(storedStandardChildOptionValue.deforme).find(
          (option) => option.isSelected
        );

        if (selectedOption?.value) {
          temp.push(`&prop.${optionsConstant.childOptions['deforme'].id}=${selectedOption.value}`);
        }
      }

      //스킨십
      if (storedStandardChildOptionValue.skinship) {
        const selectedOption = Object.values(storedStandardChildOptionValue.skinship).find(
          (option) => option.isSelected
        );

        if (selectedOption?.value) {
          temp.push(`&prop.${optionsConstant.childOptions['skinship'].id}=${selectedOption.value}`);
        }
      }

      //연령대
      if (storedStandardChildOptionValue.ageRange) {
        const selectedAgeRangeChildOptionValuesArr = Object.values(storedStandardChildOptionValue.ageRange)
          .filter((item) => item.isSelected)
          .map((item) => item.value);

        //선택한 연령대 값이 1개 이상일 경우에만 진행
        if (selectedAgeRangeChildOptionValuesArr.length > 0) {
          //연령대 값
          const selectedFrameChildOptionValuesStr = selectedAgeRangeChildOptionValuesArr.join('^');
          temp.push(`&prop.${optionsConstant.childOptions['ageRange'].id}=${selectedFrameChildOptionValuesStr}`);
        }
      }

      //중심동작부위
      if (storedStandardChildOptionValue.mainMotionPart) {
        const selectedMainMotionPartChildOptionValuesArr = Object.values(storedStandardChildOptionValue.mainMotionPart)
          .filter((item) => item.isSelected)
          .map((item) => item.value);

        //선택한 중심동작부위 값이 1개 이상일 경우에만 진행
        if (selectedMainMotionPartChildOptionValuesArr.length > 0) {
          //중심동작부위 값
          const selectedFrameChildOptionValuesStr = selectedMainMotionPartChildOptionValuesArr.join('^');
          temp.push(`&prop.${optionsConstant.childOptions['mainMotionPart'].id}=${selectedFrameChildOptionValuesStr}`);
        }
      }

      //표정
      if (storedStandardChildOptionValue.facialExpression) {
        const selectedFacialExpressionChildOptionValuesArr = Object.values(
          storedStandardChildOptionValue.facialExpression
        )
          .filter((item) => item.isSelected)
          .map((item) => item.value);

        //선택한 표정 값이 1개 이상일 경우에만 진행
        if (selectedFacialExpressionChildOptionValuesArr.length > 0) {
          //표정 값
          const selectedFrameChildOptionValuesStr = selectedFacialExpressionChildOptionValuesArr.join('^');
          temp.push(
            `&prop.${optionsConstant.childOptions['facialExpression'].id}=${selectedFrameChildOptionValuesStr}`
          );
        }
      }

      //동작구분
      if (storedStandardChildOptionValue.postureCategory) {
        const selectedPostureCategoryChildOptionValuesArr = Object.values(
          storedStandardChildOptionValue.postureCategory
        )
          .filter((item) => item.isSelected)
          .map((item) => item.value);

        //선택한 동작구분 값이 1개 이상일 경우에만 진행
        if (selectedPostureCategoryChildOptionValuesArr.length > 0) {
          //동작구분 값
          const selectedHorizontalAngledChildOptionValuesStr = selectedPostureCategoryChildOptionValuesArr.join('^');
          temp.push(
            `&prop.${optionsConstant.childOptions['postureCategory'].id}=${selectedHorizontalAngledChildOptionValuesStr}`
          );
        }
      }
    }
    //19금
    else if (locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE) {
      temp.push(`&take=${otherOutputTake}`);

      //장르 및 작품명
      if (storedGenreList.length === 0 && storedComicNameList.length === 0) {
        return '';
      } else {
        //장르
        if (storedGenreList.length > 0) {
          const selectedGenreStr = storedGenreList.join('^');
          temp.push(`&genre=${selectedGenreStr}`);
        }
        //작품명
        if (storedComicNameList.length > 0) {
          const selectedComicStr = storedComicNameList.join('^');
          temp.push(`&comic=${selectedComicStr}`);
        }
      }

      // 내가 사용한 이미지 포함 여부
      temp.push(`&isIncludeUsed=${storedAdultsOnlyChildOptionValue.isIncludeUsed}`);

      // 남이 사용한 이미지 포함 여부
      temp.push(`&isIncludeOtherUsersUsed=${storedAdultsOnlyChildOptionValue.isIncludeOtherUsersUsed}`);

      // 정렬 기준
      temp.push(`&sortType=${storedAdultsOnlyChildOptionValue.sortType}`);

      //parentOption이 GENERAL인 옵션에 대한 query param 생성
      //parentOption이 GENERAL이 아닌 옵션에 대한 query param보다 앞 쪽에 위치하여야 한다 (14차 스프린트 사항)
      //인원수, 샷구분(=19금 프레임), 가로앵글, 세로앵글
      //general이 아닌 adultsOnly store에서 가져온 값
      if (
        storedAdultsOnlyChildOptionValue.characterNumber ||
        storedAdultsOnlyChildOptionValue.frame ||
        storedAdultsOnlyChildOptionValue.angleFirst ||
        storedAdultsOnlyChildOptionValue.angleSecond
      ) {
        temp.push(`&parentOptions=${optionsConstant.parentOptions['GENERAL'].id}`);

        //인원수
        if (storedAdultsOnlyChildOptionValue.characterNumber) {
          temp.push(
            `&prop.${
              optionsConstant.childOptions['maleNumber'].id
            }=${storedAdultsOnlyChildOptionValue.characterNumber.maleNumber}&prop.${optionsConstant.childOptions['femaleNumber'].id}=${storedAdultsOnlyChildOptionValue.characterNumber.femaleNumber}`
          );
        }

        //프레임
        if (storedAdultsOnlyChildOptionValue.frame) {
          const selectedFrameChildOptionValuesArr = Object.values(storedAdultsOnlyChildOptionValue.frame)
            .filter((item) => item.isSelected)
            .map((item) => item.value);

          //선택한 프레임 값이 1개 이상일 경우에만 진행
          if (selectedFrameChildOptionValuesArr.length > 0) {
            //프레임 값
            const selectedFrameChildOptionValuesStr = selectedFrameChildOptionValuesArr.join('^');
            temp.push(`&prop.${optionsConstant.childOptions['shot'].id}=${selectedFrameChildOptionValuesStr}`);
          }
        }

        //가로앵글
        if (storedAdultsOnlyChildOptionValue.angleFirst) {
          const selectedAngleFirstChildOptionValuesArr = Object.values(storedAdultsOnlyChildOptionValue.angleFirst)
            .filter((item) => item.isSelected)
            .map((item) => item.value);

          //선택한 가로앵글 값이 1개 이상일 경우에만 진행
          if (selectedAngleFirstChildOptionValuesArr.length > 0) {
            //가로앵글 값
            const selectedAngleFirstChildOptionValuesStr = selectedAngleFirstChildOptionValuesArr.join('^');
            temp.push(
              `&prop.${optionsConstant.childOptions['angleFirst'].id}=${selectedAngleFirstChildOptionValuesStr}`
            );
          }
        }

        //세로앵글
        if (storedAdultsOnlyChildOptionValue.angleSecond) {
          const selectedAngleSecondChildOptionValuesArr = Object.values(storedAdultsOnlyChildOptionValue.angleSecond)
            .filter((item) => item.isSelected)
            .map((item) => item.value);

          //선택한 세로앵글 값이 1개 이상일 경우에만 진행
          if (selectedAngleSecondChildOptionValuesArr.length > 0) {
            //세로앵글 값
            const selectedAngleSecondChildOptionValuesStr = selectedAngleSecondChildOptionValuesArr.join('^');
            temp.push(
              `&prop.${optionsConstant.childOptions['angleSecond'].id}=${selectedAngleSecondChildOptionValuesStr}`
            );
          }
        }
      }

      //parentOption={19금}
      temp.push(`&parentOptions=${optionsConstant.parentOptions['ADULTS_ONLY'].id}`);

      // 체위
      temp.push(
        `&prop.${optionsConstant.childOptions['sexualPosition'].id}=${storedAdultsOnlyChildOptionValue.sexualPosition}`
      );

      //여체
      if (storedAdultsOnlyChildOptionValue.femaleBody) {
        const selectedFemaleBodyChildOptionValuesArr = Object.values(storedAdultsOnlyChildOptionValue.femaleBody)
          .filter((item) => item.isSelected)
          .map((item) => item.value);

        //선택한 여체이 1개 이상일 경우에만 진행
        if (selectedFemaleBodyChildOptionValuesArr.length > 0) {
          //여체
          const selectedFemaleBodyChildOptionValuesStr = selectedFemaleBodyChildOptionValuesArr.join('^');
          temp.push(`&prop.${optionsConstant.childOptions['femaleBody'].id}=${selectedFemaleBodyChildOptionValuesStr}`);
        }
      }

      //19금 신체 부위
      if (storedAdultsOnlyChildOptionValue.sexualBodyPart) {
        const selectedSexualBodyPartChildOptionValuesArr = Object.values(
          storedAdultsOnlyChildOptionValue.sexualBodyPart
        )
          .filter((item) => item.isSelected)
          .map((item) => item.value);

        //선택한 19금 신체 부위 값이 1개 이상일 경우에만 진행
        if (selectedSexualBodyPartChildOptionValuesArr.length > 0) {
          //19금 신체 부위 값
          const selectedSexualBodyPartChildOptionValuesStr = selectedSexualBodyPartChildOptionValuesArr.join('^');
          temp.push(
            `&prop.${optionsConstant.childOptions['sexualBodyPart'].id}=${selectedSexualBodyPartChildOptionValuesStr}`
          );
        }
      }

      //태그
      if (
        storedAdultsOnlyChildOptionValue.selectedMetaTags &&
        storedAdultsOnlyChildOptionValue.selectedMetaTags.length > 0
      ) {
        const selectedMetaTagNameStr = storedAdultsOnlyChildOptionValue.selectedMetaTags
          .map((item) => `metaTag=${item}`)
          .join('&');
        temp.push(`&${selectedMetaTagNameStr}`);
      }
    }
    //액션 장르
    else if (locationPathname === PathnameEnum.ACTION_GENRE_OUTPUT_PAGE) {
      temp.push(`&take=${otherOutputTake}`);

      //인원수
      //'&parentOptions=1'은 parentOption이 GENERAL이 아닌 옵션에 대한 query param보다 앞 쪽에 위치하여야 한다 (14차 스프린트 사항)
      temp.push(
        `&parentOptions=${optionsConstant.parentOptions['GENERAL'].id}&prop.${optionsConstant.childOptions['wholeGenderNumber'].id}=${wholeGenderNumber === '4+' ? '다수' : wholeGenderNumber}`
      );

      //액션 장르 차일드 옵션 값
      temp.push(
        `&parentOptions=${optionsConstant.parentOptions['ACTION'].id}&prop.${optionsConstant.childOptions['action'].id}=${actionGenreChildOptionValue}`
      );
    }
    //신체부위
    else if (locationPathname === PathnameEnum.BODY_PART_OUTPUT_PAGE) {
      const selectedBodyPartChildOptionValuesArr = Object.keys(storedBodyPartChildOptionValue).filter(
        (key) => storedBodyPartChildOptionValue[key]
      );

      //선택한 신체부위 차일드 옵션 값이 1개 이상일 경우에만 진행
      if (selectedBodyPartChildOptionValuesArr.length > 0) {
        temp.push(`&take=${otherOutputTake}`);

        //parentOption={스탠다드}
        temp.push(`&parentOptions=${optionsConstant.parentOptions['STANDARD'].id}`);

        //신체부위 차일드 옵션 값
        const selectedBodyPartChildOptionValuesStr = selectedBodyPartChildOptionValuesArr.join('^');
        temp.push(`&prop.${optionsConstant.childOptions['bodyPart'].id}=${selectedBodyPartChildOptionValuesStr}`);
      }
    }
    //표정
    else if (locationPathname === PathnameEnum.FACIAL_EXPRESSION_OUTPUT_PAGE) {
      const selectedFacialExpressionChildOptionValuesArr = Object.keys(storedFacialExpressionChildOptionValue).filter(
        (key) => storedFacialExpressionChildOptionValue[key]
      );

      //선택한 표정 차일드 옵션 값이 1개 이상일 경우에만 진행
      if (selectedFacialExpressionChildOptionValuesArr.length > 0) {
        temp.push(`&take=${otherOutputTake}`);

        //parentOption={스탠다드}
        temp.push(`&parentOptions=${optionsConstant.parentOptions['STANDARD'].id}`);

        //표정 차일드 옵션 값
        const selectedFacialExpressionChildOptionValuesStr = selectedFacialExpressionChildOptionValuesArr.join('^');
        temp.push(
          `&prop.${optionsConstant.childOptions['facialExpression'].id}=${selectedFacialExpressionChildOptionValuesStr}`
        );
      }
    }
    //기타
    else if (locationPathname === PathnameEnum.ETC_OUTPUT_PAGE) {
      const selectedEtcChildOptionValuesArr = Object.keys(storedEtcChildOptionValue).filter(
        (key) => storedEtcChildOptionValue[key]
      );

      //선택한 기타 차일드 옵션 값이 1개 이상일 경우에만 진행
      if (selectedEtcChildOptionValuesArr.length > 0) {
        temp.push(`&take=${otherOutputTake}`);

        //parentOption={기타}
        temp.push(`&parentOptions=${optionsConstant.parentOptions['ETC'].id}`);

        //기타 차일드 옵션 값
        const selectedEtcChildOptionValuesStr = selectedEtcChildOptionValuesArr.join('^');
        temp.push(`&prop.${optionsConstant.childOptions['etc'].id}=${selectedEtcChildOptionValuesStr}`);
      }
    }

    const qs = temp.join('');

    return qs;
  };

  const { data, hasNextPage, fetchNextPage, isLoading, isFetching } = useFetchImageList(
    createQueryStringForImageList(),
    {
      onSuccess: (data, variables) => {
        // pageParam이 1인 경우에만 스크롤업 실행
        if (variables.pageParam === 1) {
          window.scrollTo({
            top: 0
          });
        }
      }
    }
  );

  const { mutateAsync: putImageUseMutateAsync } = usePutImageUse((err) => {
    // PUT 요청 실패 시 캐시 데이터 복구
    if (data) {
      const currentQueryKey = ['get', `/images${createQueryStringForImageList()}`];

      // 실패한 요청의 imageId 추출
      const failedImageId = Number(err.config?.url?.split('/').pop());

      // 이전 상태로 데이터 복구
      const restoredPages = data.pages.map((page) => ({
        ...page,
        data: {
          ...page.data,
          data: page.data.data.map((item: IImageListItem) => {
            if (item.id === failedImageId) {
              return {
                ...item,
                isUsed: !item.isUsed, // 토글된 상태를 다시 원래대로
                usedCount: item.isUsed ? item.usedCount - 1 : item.usedCount + 1 // 변경된 usedCount를 다시 원래대로
              };
            }
            return item;
          })
        }
      }));

      // 캐시 업데이트
      queryClient.setQueryData(currentQueryKey, (oldData: any) => ({
        pageParams: oldData.pageParams,
        pages: restoredPages
      }));

      // 리렌더링 트리거
      queryClient.invalidateQueries({
        queryKey: currentQueryKey,
        exact: true,
        refetchType: 'none'
      });
    }
  });

  const flattenImageList = () => {
    const dataArrLength = data?.pages.length;
    const flattenedArr: IImageListItem[] = [];

    if (dataArrLength) {
      let i = 0;
      while (i < dataArrLength) {
        if (data.pages[i].data.data.length > 0) {
          data.pages[i].data.data.map((item: IImageListItem) => {
            flattenedArr.push(item);
          });
        }
        i++;
      }
      return flattenedArr;
    } else {
      return [];
    }
  };

  const handleImageClick = (
    imageId: number,
    s3Url: string,
    sequenceInfo: ISequenceInfo | null,
    isUsed?: boolean,
    usedCount?: number,
    isCollected?: boolean
  ) => {
    const isStandardOutputPage = locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE;
    const isAdultsOnlyOutputPage = locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE;

    setImageModalType({
      needSequence: Boolean(sequenceInfo),
      needSimilar: isAdultsOnlyOutputPage || isStandardOutputPage
    });
    setSelectedImageInfo({ id: imageId, s3Url: s3Url, isUsed: isUsed, usedCount: usedCount, isCollected: isCollected });
    setSequenceInfo(sequenceInfo);
    setIsImageModalVisible(true);
  };

  const handleImageModalClose = () => {
    setIsImageModalVisible(false);
  };

  // 스탠다드 및 19금 Output 페이지 '내가 사용한 이미지' 버튼 클릭시 실행
  const handleIncludeUsedToggle = () => {
    if (locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE) {
      onSetStandard({ isIncludeUsed: !storedStandardChildOptionValue.isIncludeUsed });
    } else if (locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE) {
      onSetAdultsOnly({ isIncludeUsed: !storedAdultsOnlyChildOptionValue.isIncludeUsed });
    }
  };

  // 스탠다드 및 19금 Output 페이지 '남이 사용한 이미지' 버튼 클릭시 실행
  const handleIncludeOtherUsersUsedToggle = () => {
    if (locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE) {
      onSetStandard({ isIncludeOtherUsersUsed: !storedStandardChildOptionValue.isIncludeOtherUsersUsed });
    } else if (locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE) {
      onSetAdultsOnly({ isIncludeOtherUsersUsed: !storedAdultsOnlyChildOptionValue.isIncludeOtherUsersUsed });
    }
  };

  // 스탠다드 및 19금 Output 페이지 이미지 '사용' 버튼 클릭시 실행
  const handleImageUseButtonClick = (imageId: number, isUsed: boolean, usedCount: number, isCollected?: boolean) => {
    if (data) {
      // 모달 내 isUsed 및 usedCount값 업데이트
      if (isImageModalVisible) {
        setSelectedImageInfo({
          id: selectedImageInfo?.id ?? 0,
          s3Url: selectedImageInfo?.s3Url ?? '',
          isUsed: !isUsed,
          usedCount: !isUsed ? usedCount + 1 : usedCount - 1,
          isCollected: Boolean(isCollected)
        });
      }

      // 1. useFetchImageList와 동일한 쿼리 키 사용
      const currentQueryKey = ['get', `/images${createQueryStringForImageList()}`];

      // 2. 새로운 페이지 데이터 생성
      const newPages = data.pages.map((page) => ({
        ...page,
        data: {
          ...page.data,
          data: page.data.data.map((item: IImageListItem) => {
            if (item.id === imageId) {
              return {
                ...item,
                isUsed: !isUsed,
                usedCount: !isUsed ? usedCount + 1 : usedCount - 1
              };
            }
            return item;
          })
        }
      }));

      // 3. 캐시 업데이트
      queryClient.setQueryData(currentQueryKey, (oldData: any) => ({
        pageParams: oldData.pageParams,
        pages: newPages
      }));

      // 4. 쿼리 무효화하여 리렌더링 트리거
      queryClient.invalidateQueries({
        queryKey: currentQueryKey,
        exact: true,
        refetchType: 'none' // 실제 API 호출은 하지 않고 캐시만 업데이트
      });

      // 5. PUT /images/used-image/:id 요청
      putImageUseMutateAsync(imageId).then(() => {
        // 요청 성공시 토스트 메세지 보여주기
        showToast(isUsed ? ToastMessageEnum.CANCEL_IMAGE_USE : ToastMessageEnum.ADD_IMAGE_USE);
      });
    }
  };

  // 이미지 리스트 다음 페이지 호출
  useEffect(() => {
    if (outputBottomRefCurrent) {
      const observerCallback: IntersectionObserverCallback = (entries) => {
        entries.forEach((entry) => {
          //target이 화면에 관찰되고, 다음페이지가 있고, API 요청 처리중이 아니라면 다음페이지를 호출
          if (entry.isIntersecting && hasNextPage && !isLoading && !isFetching) {
            fetchNextPage();
          }
        });
      };

      //ointersection observer 인스턴스 생성
      const observer = new IntersectionObserver(observerCallback, {
        threshold: 1.0
      });

      // 타겟 관찰 시작
      observer.observe(outputBottomRefCurrent);

      // 관찰 멈춤
      return () => observer.unobserve(outputBottomRefCurrent);
    }
  }, [outputBottomRefCurrent, hasNextPage, isLoading, isFetching, fetchNextPage]);

  return {
    //general
    characterNumber,
    setCharacterNumber,
    storedGeneralChildOptionValue,
    onSetGeneral,

    //standard
    storedStandardChildOptionValue,
    onSetStandard,

    //special - adults only
    storedAdultsOnlyChildOptionValue,
    onSetAdultsOnly,
    handleIncludeUsedToggle,
    handleIncludeOtherUsersUsedToggle,
    handleImageUseButtonClick,

    //special - action genre
    storedActionGenre,
    onSetActionGenre,

    //special - body part
    storedBodyPartChildOptionValue,
    onSetBodyPart,

    //special - facial expression
    storedFacialExpressionChildOptionValue,
    onSetFacialExpression,

    //special - etc
    storedEtcChildOptionValue,
    onSetEtc,

    locationPathname,
    data,
    isLoading,
    flattenImageList,
    outputBottomRef,
    createQueryStringForImageList,

    isImageModalVisible,
    imageModalType,
    selectedImageInfo,
    sequenceInfo,
    handleImageClick,
    handleImageModalClose
  };
};
